/**
 * KPI Cards Component
 * Displays key performance indicators for License Intelligence dashboard
 * Renders a horizontal ribbon layout with 5 KPI cards showing metrics and trends
 */

import React from 'react';
import { Icon, Typography } from '@saviynt/design-system';

import type { KPICardsProps } from './type';

import './KPICards.css';

/**
 * KPI Cards component displaying key metrics with trends
 * @param {KPICardsProps} props - Component props
 * @param {KPICardData[]} props.cards - Array of KPI card data
 * @returns {React.ReactElement} Horizontal ribbon of KPI cards with trends and dividers
 */
function KPICards({ cards }: KPICardsProps): React.ReactElement {
  const getRiskInfoClassName = (color: string): string => {
    switch (color) {
      case 'critical':
        return 'kpiRiskInfoCritical';
      case 'medium':
        return 'kpiRiskInfoMedium';
      default:
        return '';
    }
  };

  return (
    <div className='kpiCardsContainer'>
      {cards.map((card, index) => (
        <React.Fragment key={card.label}>
          <div className='kpiCard'>
            {/* KPI Header: Value and Label */}
            <div className='kpiCardHeader'>
              <Typography kind='display4-light'>{card.value}</Typography>
              <Typography kind='body1-bold' color='#b9c3d1'>
                {card.label}
              </Typography>
            </div>

            {/* KPI Footer: Trend or Risk Info */}
            <div className='kpiCardFooter'>
              {card.trend ? (
                <div className='kpiTrendSection'>
                  <Typography kind='body1' className='kpiTrendValue'>
                    {card.trend.value}
                  </Typography>
                  <div className='kpiTrendBadge'>
                    <Icon
                      kind={
                        card.trend.direction === 'up' ? 'TrendUp' : 'TrendDown'
                      }
                      size='small'
                    />
                  </div>
                  <Typography kind='body2' className='kpiTrendPeriod'>
                    {card.trend.period}
                  </Typography>
                </div>
              ) : (
                <Typography kind='body2' className='kpiRiskInfo'>
                  {Array.isArray(card.riskInfo)
                    ? card.riskInfo.map((part) => (
                        <span
                          key={`${card.label}-risk-${part.text}`}
                          className={getRiskInfoClassName(
                            part.color ?? 'default'
                          )}>
                          {part.text}
                        </span>
                      ))
                    : card.riskInfo}
                </Typography>
              )}
            </div>
          </div>
          {index < cards.length - 1 && <div className='kpiDivider' />}
        </React.Fragment>
      ))}
    </div>
  );
}

export default KPICards;
